import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateAppToken, validateAppToken } from '../../../../store/AppTokenAction';
import Loading from '../../../misc/Loading';
import { Input, Label, Button, Form } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { CoreAppToken, CoreAppTokenValidation } from '../../../../models/CoreAppToken';
import { RequestState } from '../../../../models/RequestState';
import StatusCode from '../../../../util/StatusCode';
import Copy from '../../../misc/Copy';
import { useTranslation, Trans } from 'react-i18next';
import { RootState } from '../../../../store/store';


interface AppTokenGoogleServiceAccountProps {
    appToken: CoreAppToken,
    disableInput: boolean
}

const AppTokenGoogleServiceAccount = (props: AppTokenGoogleServiceAccountProps) => {
    const [userName, setUserName] = useState('');
    const [updatingApptoken, setUpdatingAppToken] = useState(false);
    const { t } = useTranslation("configuration")

    const appTokenValidationRequestState = useSelector<RootState, RequestState<CoreAppTokenValidation>>(x => x.token.appTokenValidation);

    const dispatch = useDispatch();

    const generateGoogleServiceAccount: React.FormEventHandler<HTMLFormElement> = async (e) => {
        e.preventDefault();

        let appTokenBody = {
            appRegistration: props.appToken.appRegistration,
            userName: userName
        }

        setUpdatingAppToken(true)
        await updateAppToken(props.appToken.id, appTokenBody)(dispatch);
        setUpdatingAppToken(false);

        validateUpdate();
    }

    const validateUpdate = async () => {
        await validateAppToken(props.appToken.id)(dispatch);
    }

    const unauthorizedAccess = (appTokenValidation: CoreAppTokenValidation) => {
        return (
            <div>
                <p><FontAwesomeIcon icon="triangle-exclamation" className="apptoken-configuration__status-icon--warning" />{t('appTokenConfiguration.configurationRequired')}</p>
                <p>{t('appTokenConfiguration.googleServiceAccount.instructions.intro')}</p>
                <p>{t('appTokenConfiguration.googleServiceAccount.instructions.follow')}</p>
                <ol>
                    <li>
                        <Trans
                            t={t}
                            i18nKey="appTokenConfiguration.googleServiceAccount.instructions.1"
                            components={{
                                a: <a href={appTokenValidation.flowSyncUXUri} target="_blank" rel="noopener noreferrer">x</a>
                            }}
                        />
                    </li>
                    <li>{t('appTokenConfiguration.googleServiceAccount.instructions.2')}</li>
                    <li>{t('appTokenConfiguration.googleServiceAccount.instructions.3', { clientId: appTokenValidation.clientId })}</li>
                    <li>{t('appTokenConfiguration.googleServiceAccount.instructions.4')}: {appTokenValidation.scopes && <Copy value={appTokenValidation.scopes?.join(',')} />}</li>
                    <ul>
                        {appTokenValidation.scopes?.join(', ')}
                    </ul>
                    <li>{t('appTokenConfiguration.googleServiceAccount.instructions.5')}</li>
                </ol>

                <p>{t('appTokenConfiguration.googleServiceAccount.instructions.outro')}</p>
                <div className="apptoken-configuration__button-container">
                    <Button color="primary" disabled={props.disableInput} onClick={validateUpdate}>{t('appTokenConfiguration.googleServiceAccount.validate')}</Button>
                </div>
            </div>
        )
    }

    if (appTokenValidationRequestState.code === StatusCode.NONE || appTokenValidationRequestState.code === StatusCode.PENDING) {
        return (<Loading />);
    }

    if (appTokenValidationRequestState.code === StatusCode.ERROR) {
        return (
            <div>
                <p>{t('appTokenConfiguration.validationError.failed')}</p>
                <p>{t('appTokenConfiguration.validationError.response', { code: appTokenValidationRequestState.error.code })}</p>
                <Form onSubmit={generateGoogleServiceAccount}>
                    <Label for="userName">{t('appTokenConfiguration.googleServiceAccount.serviceAccount')}: </Label>
                    <Input name="userName" disabled={props.disableInput} onChange={(e) => setUserName(e.target.value.trim())} value={userName} />
                    <div className="apptoken-configuration__button-container">
                        <Button color="primary" type="submit">{t('appTokenConfiguration.googleServiceAccount.configure')}</Button>
                        {updatingApptoken && <Loading className="push--left" />}
                    </div>
                </Form>
            </div>
        )
    }

    const appTokenValidation = appTokenValidationRequestState.data;

    if (!appTokenValidation.valid && appTokenValidation.reason === 'UnauthorizedAccess') {
        return unauthorizedAccess(appTokenValidation);
    }

    if (!appTokenValidation.valid) {
        return (
            <div>
                <p><FontAwesomeIcon icon="triangle-exclamation" className="apptoken-configuration__status-icon--warning" />{t('appTokenConfiguration.configurationRequired')}</p>
                {props.appToken.userName && <p>{t('appTokenConfiguration.googleServiceAccount.invalidUsername', { username: props.appToken.userName })}</p>}
                <Form onSubmit={generateGoogleServiceAccount}>
                    <Label>{t('appTokenConfiguration.googleServiceAccount.serviceAccount')}: </Label>
                    <Input name="userName" disabled={props.disableInput} onChange={(e) => setUserName(e.target.value.trim())} value={userName} />
                    <div className="apptoken-configuration__button-container">
                        <Button className="apptoken-configuration__button" color="primary" disabled={props.disableInput} type="submit">{t('appTokenConfiguration.googleServiceAccount.configure')}</Button>
                        {updatingApptoken && <Loading className="push--left" />}
                    </div>
                </Form>
            </div>
        )
    }

    return (<div>
        <div className='apptoken-configuration__status'>
            <FontAwesomeIcon icon="check" className="apptoken-configuration__status-icon--ok" />{t('appTokenConfiguration.accessConfigured')}
        </div>
        <div className='apptoken-configuration__status'>
            <FontAwesomeIcon icon="user" className="apptoken-configuration__status-icon" />{t('appTokenConfiguration.googleServiceAccount.serviceAccount')}: {props.appToken.userName}
        </div>
    </div>);
}

export default AppTokenGoogleServiceAccount;
